import { Loader2, AlertCircle } from 'lucide-react'
import { useState, useEffect } from 'react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { RecordFormFields, useRecordForm } from '@/features/records/components/RecordFormFields'
import { useFileUploadConfig } from '@/hooks/useFileUpload'
import { useAllForms, useFormById } from '@/hooks/useForm'
import { useRecords } from '@/hooks/useRecords'
import { uploadRecordValueFile } from '@/services/api/file.service'
import type { FormField } from '@/types/api/form'
import { cn } from '@/utils/cn'
import { handleApiError } from '@/utils/error-parser'

const TITLE = 'New Record'
const DESCRIPTION = 'Fill in the fields below to create a new record.'

// ── NewRecordDialog ───────────────────────────────────────────────────────────

interface Props {
  open: boolean
  onClose: () => void
  recordGroupId: string
}

export function NewRecordDialog({ open, onClose, recordGroupId }: Props) {
  const { data: allForms = [], isLoading: formsLoading } = useAllForms(recordGroupId)
  const { create, isCreating } = useRecords(recordGroupId)

  // Sort descending so index 0 = latest version
  const sortedForms = [...allForms].sort((a, b) => b.version - a.version)

  const [selectedFormId, setSelectedFormId] = useState<string>('')
  // Fields whose upload dialog is open: submitting waits until they are closed.
  const [uploadingFields, setUploadingFields] = useState<Set<string>>(new Set())
  const uploading = uploadingFields.size > 0
  const { data: fileConfig } = useFileUploadConfig()

  // Seed selectedFormId once forms resolve (pick latest)
  useEffect(() => {
    if (sortedForms.length > 0 && !selectedFormId) {
      setSelectedFormId(sortedForms[0].id)
    }
  }, [sortedForms, selectedFormId])

  const { data: selectedForm, isLoading: formDetailLoading } = useFormById(
    selectedFormId || ''
  )
  const form = useRecordForm(selectedForm?.fields)
  const { reset } = form

  // Reset values whenever the selected version changes
  useEffect(() => {
    reset()
    setUploadingFields(new Set())
  }, [selectedFormId, reset])

  const handleSubmit = () => {
    if (isCreating || uploading) return
    if (!form.validate() || !selectedFormId) return

    create(
      { formId: selectedFormId, values: form.payload() },
      {
        onSuccess: () => {
          toast.success('Record created successfully')
          onClose()
        },
        onError: (error) => {
          handleApiError(error)
        },
      }
    )
  }

  const isLoading = formsLoading || formDetailLoading

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose() }}>
      <DialogContent
        className={cn(
          'flex h-[100dvh] w-screen max-w-none flex-col gap-0 rounded-none p-0',
          'sm:h-auto sm:max-h-[90vh] sm:w-full sm:rounded-lg',
          form.layout.dialog
        )}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
            e.preventDefault()
            handleSubmit()
          }
        }}
      >
        <DialogHeader className="space-y-3 border-b px-6 pb-4 pt-6">
          <div className="space-y-1.5">
            <DialogTitle>{TITLE}</DialogTitle>
            <DialogDescription>{DESCRIPTION}</DialogDescription>
          </div>

          {/* Version selector — only shown when multiple versions exist */}
          {sortedForms.length > 1 && (
            <div>
              <Label htmlFor="new-record-form-version" className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-neutral-400">
                Form Version
              </Label>
              <Select
                value={selectedFormId}
                onValueChange={(v) => setSelectedFormId(v)}
              >
                <SelectTrigger id="new-record-form-version" className="sm:max-w-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {sortedForms.map((f, idx) => (
                    <SelectItem key={f.id} value={f.id}>
                      Version {f.version}
                      {idx === 0 ? (
                        <span className="ml-2 rounded-full bg-primary-100 px-1.5 py-0.5 text-[10px] font-semibold text-primary-700">
                          Latest
                        </span>
                      ) : null}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </DialogHeader>

        {/* Fields */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-neutral-400" />
            </div>
          ) : !selectedForm ? (
            <div className="flex items-center gap-3 rounded-lg border border-destructive-200 bg-destructive-50 p-4">
              <AlertCircle className="h-5 w-5 shrink-0 text-destructive-500" />
              <p className="text-sm text-destructive-700">
                Could not load form fields. Please try again.
              </p>
            </div>
          ) : form.dataFields.length === 0 ? (
            <EmptyForm hasFields={selectedForm.fields.length > 0} />
          ) : (
            <RecordFormFields
              form={form}
              disabled={isCreating}
              uploadFile={(field) => (file, options) => uploadRecordValueFile(recordGroupId, field.id, file, options)}
              fileConfig={fileConfig}
              onUploadingChange={(fieldId, open) => setUploadingFields((prev) => {
                const next = new Set(prev)
                if (open) next.add(fieldId)
                else next.delete(fieldId)
                return next
              })}
            />
          )}
        </div>

        <DialogFooter className="items-center gap-2 border-t px-6 py-4 sm:justify-between sm:space-x-0">
          <p className="text-xs text-muted-foreground" aria-live="polite">{form.requiredStatus}</p>
          <div className="flex flex-col-reverse gap-2 sm:flex-row">
            <Button variant="outline" onClick={onClose} disabled={isCreating}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={isCreating || uploading || isLoading || !selectedForm || form.visibleDataFields.length === 0}
            >
              {isCreating ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating…
                </>
              ) : (
                'Create Record'
              )}
            </Button>
          </div>
        </DialogFooter>

      </DialogContent>
    </Dialog>
  )
}

function EmptyForm({ hasFields }: { hasFields: boolean }) {
  return (
    <div className="flex flex-col items-center py-10 text-center">
      <p className="text-sm font-medium text-neutral-600">
        {hasFields ? 'You cannot fill in any field of this form.' : 'This form has no fields.'}
      </p>
      <p className="mt-1 text-xs text-neutral-400">
        {hasFields ? 'Ask an admin for access to its fields.' : 'Configure the form in the Form Designer first.'}
      </p>
    </div>
  )
}

// ── Form designer preview ─────────────────────────────────────────────────────

/**
 * The "New Record" dialog as it will look for the form being designed, drawn in place rather than as
 * a modal. Interactive; "Create Record" only checks the answers and saves nothing.
 */
export function NewRecordPreview({ fields }: { fields: FormField[] }) {
  const form = useRecordForm(fields)

  const handleCreate = () => {
    if (form.validate()) toast.success('All answers are valid. This is a preview, so nothing was saved.')
  }

  return (
    <div className={cn('mx-auto w-full overflow-hidden rounded-lg border bg-white shadow-lg', form.layout.panel)}>
      <div className="space-y-1.5 border-b px-6 pb-4 pt-6">
        <h2 className="text-lg font-semibold leading-none tracking-tight">{TITLE}</h2>
        <p className="text-sm text-muted-foreground">{DESCRIPTION}</p>
      </div>
      <div className="px-6 py-5">
        {form.dataFields.length === 0
          ? <EmptyForm hasFields={false} />
          : <RecordFormFields form={form} />}
      </div>
      <div className="flex flex-col-reverse items-center gap-2 border-t px-6 py-4 sm:flex-row sm:justify-between">
        <p className="text-xs text-muted-foreground" aria-live="polite">{form.requiredStatus}</p>
        <div className="flex flex-col-reverse gap-2 sm:flex-row">
          <Button variant="outline" onClick={form.reset}>Clear</Button>
          <Button onClick={handleCreate} disabled={form.visibleDataFields.length === 0}>Create Record</Button>
        </div>
      </div>
    </div>
  )
}
